// lib/auth.ts
import { LOCAL_STORAGE_KEYS } from "./constants";
import { AuthContextUser } from "../types/auth";

export const sanitizeRedirectUrl = (
  target: string | null | undefined
): string | null => {
  if (!target) return null;

  const trimmedTarget = target.trim();
  if (!trimmedTarget) return null;

  // Allow only app-internal paths to avoid open redirects.
  if (trimmedTarget.startsWith("/") && !trimmedTarget.startsWith("//")) {
    return trimmedTarget;
  }

  if (typeof window !== "undefined") {
    try {
      const parsed = new URL(trimmedTarget);
      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    } catch {
      return null;
    }
  }

  return null;
};

export const storeToken = (token: string): void => {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN, token);
    } catch (error) {
      console.error("Error storing token in localStorage:", error);
    }
  }
};

export const getToken = (): string | null => {
  if (typeof window !== "undefined") {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN);
    } catch (error) {
      console.error("Error retrieving token from localStorage:", error);
      return null;
    }
  }
  return null;
};

export const removeToken = (): void => {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN);
    } catch (error) {
      console.error("Error removing token from localStorage:", error);
    }
  }
};

// --- MODIFIED: Store more user data fields ---
export const storeUserData = (user: AuthContextUser): void => {
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ID, user.id ?? "");
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ROLE, user.role);
      // Store boolean flags as strings
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.IS_KYC_SUBMITTED,
        String(user.isKycSubmitted)
      );
      // Store phone and telegramIdLinked if available
      if (user.phone !== undefined) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.USER_PHONE, user.phone ?? "");
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_PHONE); // Remove if not provided
      }
      if (user.telegramIdLinked !== undefined) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.TELEGRAM_ID_LINKED,
          String(user.telegramIdLinked)
        );
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TELEGRAM_ID_LINKED); // Remove if not provided
      }
      if (user.slackIdLinked !== undefined) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.SLACK_ID_LINKED,
          String(user.slackIdLinked)
        );
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.SLACK_ID_LINKED);
      }
      // Note: We are NOT storing role_id, name, channels, referralCode, loginId, belongs_to here
      // These should be fetched via /users/me if needed beyond initial auth state.
    } catch (error) {
      console.error("Error storing user data in localStorage:", error);
    }
  }
};

// --- MODIFIED: Retrieve more user data fields ---
export const getUserData = (): {
  id: string | null;
  role: string | null;
  isKycSubmitted: boolean | null;
  phone: string | null; // Retrieve phone
  telegramIdLinked: boolean | null; // Retrieve telegram linked status
  slackIdLinked: boolean | null;
} => {
  if (typeof window !== "undefined") {
    try {
      const kycStatus = localStorage.getItem(
        LOCAL_STORAGE_KEYS.IS_KYC_SUBMITTED
      );
      const telegramStatus = localStorage.getItem(
        LOCAL_STORAGE_KEYS.TELEGRAM_ID_LINKED
      );
      const slackStatus = localStorage.getItem(
        LOCAL_STORAGE_KEYS.SLACK_ID_LINKED
      );
      return {
        id: localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID),
        role: localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ROLE),
        // Convert stored strings back to boolean, default to null if not found/invalid
        isKycSubmitted:
          kycStatus === "true" ? true : kycStatus === "false" ? false : null,
        phone: localStorage.getItem(LOCAL_STORAGE_KEYS.USER_PHONE), // Retrieve phone
        telegramIdLinked:
          telegramStatus === "true"
            ? true
            : telegramStatus === "false"
            ? false
            : null, // Retrieve telegram status
        slackIdLinked:
          slackStatus === "true"
            ? true
            : slackStatus === "false"
            ? false
            : null,
      };
    } catch (error) {
      console.error("Error retrieving user data from localStorage:", error);
      return {
        id: null,
        role: null,
        isKycSubmitted: null,
        phone: null,
        telegramIdLinked: null,
        slackIdLinked: null,
      }; // Return defaults on error
    }
  }
  // Return defaults if window is undefined (server-side)
  return {
    id: null,
    role: null,
    isKycSubmitted: null,
    phone: null,
    telegramIdLinked: null,
    slackIdLinked: null,
  };
};

// --- MODIFIED: Remove more user data fields ---
export const removeUserData = (): void => {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_ID);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_ROLE);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.IS_KYC_SUBMITTED);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_PHONE); // Remove phone
      localStorage.removeItem(LOCAL_STORAGE_KEYS.TELEGRAM_ID_LINKED); // Remove telegram status
      localStorage.removeItem(LOCAL_STORAGE_KEYS.SLACK_ID_LINKED);
    } catch (error) {
      console.error("Error removing user data from localStorage:", error);
    }
  }
};
// --- END MODIFIED ---

export const storeRedirectUrl = (url: string): void => {
  const safeRedirectUrl = sanitizeRedirectUrl(url);
  if (typeof window !== "undefined" && safeRedirectUrl) {
    try {
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN,
        safeRedirectUrl
      );
      console.log("Stored redirect URL:", safeRedirectUrl);
    } catch (error) {
      console.error("Error storing redirect URL in localStorage:", error);
    }
  }
};

export const getRedirectUrl = (): string | null => {
  if (typeof window !== "undefined") {
    try {
      const storedRedirect = localStorage.getItem(
        LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN
      );
      return sanitizeRedirectUrl(storedRedirect);
    } catch (error) {
      console.error("Error retrieving redirect URL from localStorage:", error);
      return null;
    }
  }
  return null;
};

export const removeRedirectUrl = (): void => {
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
      console.log("Removed redirect URL from localStorage.");
    } catch (error) {
      console.error("Error removing redirect URL from localStorage:", error);
    }
  }
};

export const clearAuthData = (): void => {
  removeToken();
  removeUserData();
};
